
import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Loader2, ChevronRight, ChevronLeft, GitBranch, Check, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';
import { cn } from '@/lib/utils';

interface AppFormWizardProps {
    isOpen: boolean;
    onClose: () => void;
    /** Callback when app is created */
    onAppCreated?: () => void;
}

interface WizardStep {
    id: string;
    title: string;
    description: string;
}

const WIZARD_STEPS: WizardStep[] = [
    {
        id: 'basic',
        title: 'Basic Information',
        description: 'Enter the application name and details',
    },
    {
        id: 'bitbucket',
        title: 'Bitbucket Integration',
        description: 'Connect to a Bitbucket repository (optional)',
    },
];

export default function AppFormWizard({
    isOpen,
    onClose,
    onAppCreated,
}: AppFormWizardProps) {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [currentStep, setCurrentStep] = useState(0);

    // Step 1: Basic Information
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [slug, setSlug] = useState('');

    // Step 2: Bitbucket Integration
    const [bitbucketWorkspace, setBitbucketWorkspace] = useState('');
    const [bitbucketRepo, setBitbucketRepo] = useState('');
    const [bitbucketBranch, setBitbucketBranch] = useState('main');
    const [bitbucketPath, setBitbucketPath] = useState('resources/AI');
    const [enableBitbucket, setEnableBitbucket] = useState(false);

    // Reset form when opened
    useEffect(() => {
        if (isOpen) {
            setErrors({});
            setCurrentStep(0);
            setName('');
            setDescription('');
            setSlug('');
            setBitbucketWorkspace('');
            setBitbucketRepo('');
            setBitbucketBranch('main');
            setBitbucketPath('resources/AI');
            setEnableBitbucket(false);
        }
    }, [isOpen]);

    // Auto-generate slug from name
    const handleNameChange = (value: string) => {
        setName(value);
        const generatedSlug = value
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
        setSlug(generatedSlug);
    };

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 0) {
            if (!name.trim()) {
                newErrors.name = 'Application name is required';
            }
            if (!slug.trim()) {
                newErrors.slug = 'Slug is required';
            } else if (!/^[a-z0-9-]+$/.test(slug)) {
                newErrors.slug = 'Slug must only contain lowercase letters, numbers, and hyphens';
            }
        }

        if (step === 1 && enableBitbucket) {
            if (!bitbucketWorkspace.trim()) {
                newErrors.bitbucketWorkspace = 'Workspace is required when Bitbucket integration is enabled';
            } else if (!/^[a-zA-Z0-9_-]+$/.test(bitbucketWorkspace)) {
                newErrors.bitbucketWorkspace = 'Invalid workspace format. Use alphanumeric characters and hyphens.';
            }
            if (!bitbucketRepo.trim()) {
                newErrors.bitbucketRepo = 'Repository is required when Bitbucket integration is enabled';
            } else if (!/^[a-zA-Z0-9_.-]+$/.test(bitbucketRepo)) {
                newErrors.bitbucketRepo = 'Invalid repository format. Use alphanumeric characters, dots, underscores, and hyphens.';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) return;

        setLoading(true);
        setErrors({});

        const formData = {
            app_key: slug.trim(),
            name: name.trim(),
            description: description.trim() || null,
            config: enableBitbucket ? {
                bitbucket: {
                    workspace: bitbucketWorkspace.trim(),
                    repository: bitbucketRepo.trim(),
                    branch: bitbucketBranch.trim(),
                    path: bitbucketPath.trim(),
                },
            } : null,
        };

        try {
            await api.post('/applications', formData);
            onAppCreated?.();
            setLoading(false);
            onClose();
            if (!onAppCreated) {
                window.location.reload();
            }
        } catch (err: unknown) {
            const validationErrors: Record<string, string> = (err as Record<string, unknown> & { response?: { data?: { errors?: Record<string, string> } } })?.response?.data?.errors ?? {};
            logger.error('Failed to create application:', { errors: validationErrors });
            setErrors(validationErrors);
            setLoading(false);
        }
    };

    const inputClassName = "w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";
    const errorInputClassName = "w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

    const isLastStep = currentStep === WIZARD_STEPS.length - 1;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                                <Box className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold text-slate-900">
                                    Create Application
                                </h2>
                                <p className="text-sm text-slate-500">
                                    Step {currentStep + 1} of {WIZARD_STEPS.length}: {WIZARD_STEPS[currentStep].title}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Step Indicators */}
                    <div className="flex items-center gap-2 mt-4">
                        {WIZARD_STEPS.map((step, index) => (
                            <div key={step.id} className="flex items-center">
                                <div
                                    className={cn(
                                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                                        index < currentStep
                                            ? 'bg-emerald-500 text-white'
                                            : index === currentStep
                                                ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500'
                                                : 'bg-slate-100 text-slate-400'
                                    )}
                                >
                                    {index < currentStep ? <Check size={16} /> : index + 1}
                                </div>
                                {index < WIZARD_STEPS.length - 1 && (
                                    <div
                                        className={cn(
                                            'mx-2 h-0.5 w-12 transition-colors',
                                            index < currentStep ? 'bg-emerald-500' : 'bg-slate-200'
                                        )}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto p-8 bg-white">
                    <AnimatePresence mode="wait">
                        {currentStep === 0 && (
                            <motion.div
                                key="step-basic"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                className="space-y-6 max-w-2xl mx-auto"
                            >
                                <div className="space-y-2">
                                    <Label>Application Name <span className="text-red-500">*</span></Label>
                                    <Input
                                        placeholder="e.g. My Awesome Application"
                                        value={name}
                                        onChange={e => handleNameChange(e.target.value)}
                                        className={errors.name ? errorInputClassName : inputClassName}
                                        autoFocus
                                    />
                                    {errors.name && (
                                        <p className="text-xs text-red-500 flex items-center gap-1">
                                            <AlertCircle size={12} />
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>
                                        Slug (Capability Registry Name) <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        placeholder="e.g. my-awesome-app"
                                        value={slug}
                                        onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                                        className={`font-mono text-xs ${errors.slug ? errorInputClassName : inputClassName}`}
                                    />
                                    <p className="text-xs text-slate-400">
                                        Unique identifier used in the capability registry. Auto-generated from name.
                                    </p>
                                    {errors.slug && (
                                        <p className="text-xs text-red-500 flex items-center gap-1">
                                            <AlertCircle size={12} />
                                            {errors.slug}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <textarea
                                        placeholder="Brief description of what this application does..."
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={4}
                                        className={`${inputClassName} resize-none`}
                                    />
                                </div>
                            </motion.div>
                        )}

                        {currentStep === 1 && (
                            <motion.div
                                key="step-bitbucket"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                className="space-y-6 max-w-2xl mx-auto"
                            >
                                {/* Bitbucket Enable Toggle */}
                                <div
                                    className={cn(
                                        'flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all',
                                        enableBitbucket
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-slate-200 hover:border-slate-300'
                                    )}
                                    onClick={() => setEnableBitbucket(!enableBitbucket)}
                                >
                                    <div className={cn(
                                        'flex h-12 w-12 items-center justify-center rounded-lg',
                                        enableBitbucket ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                                    )}>
                                        <GitBranch size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-slate-900">Enable Bitbucket Integration</h3>
                                        <p className="text-sm text-slate-500">
                                            Sync AI capabilities from a Bitbucket repository
                                        </p>
                                    </div>
                                    <div className={cn(
                                        'h-6 w-11 rounded-full p-0.5 transition-colors',
                                        enableBitbucket ? 'bg-emerald-500' : 'bg-slate-200'
                                    )}>
                                        <div className={cn(
                                            'h-5 w-5 rounded-full bg-white shadow transition-transform',
                                            enableBitbucket ? 'translate-x-5' : 'translate-x-0'
                                        )} />
                                    </div>
                                </div>

                                {enableBitbucket && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="space-y-6 pt-4 border-t border-slate-100"
                                    >
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <Label>Workspace <span className="text-red-500">*</span></Label>
                                                <Input
                                                    placeholder="e.g. my-workspace"
                                                    value={bitbucketWorkspace}
                                                    onChange={e => setBitbucketWorkspace(e.target.value)}
                                                    className={`font-mono text-sm ${errors.bitbucketWorkspace ? errorInputClassName : inputClassName}`}
                                                />
                                                <p className="text-xs text-slate-400">
                                                    Your Bitbucket workspace slug
                                                </p>
                                                {errors.bitbucketWorkspace && (
                                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                                        <AlertCircle size={12} />
                                                        {errors.bitbucketWorkspace}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Repository <span className="text-red-500">*</span></Label>
                                                <Input
                                                    placeholder="e.g. ai-workflows"
                                                    value={bitbucketRepo}
                                                    onChange={e => setBitbucketRepo(e.target.value)}
                                                    className={`font-mono text-sm ${errors.bitbucketRepo ? errorInputClassName : inputClassName}`}
                                                />
                                                <p className="text-xs text-slate-400">
                                                    Repository slug
                                                </p>
                                                {errors.bitbucketRepo && (
                                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                                        <AlertCircle size={12} />
                                                        {errors.bitbucketRepo}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <Label>Branch</Label>
                                                <Input
                                                    placeholder="main"
                                                    value={bitbucketBranch}
                                                    onChange={e => setBitbucketBranch(e.target.value)}
                                                    className={`font-mono text-sm ${inputClassName}`}
                                                />
                                                <p className="text-xs text-slate-400">Default: main</p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Path</Label>
                                                <Input
                                                    placeholder="resources/AI"
                                                    value={bitbucketPath}
                                                    onChange={e => setBitbucketPath(e.target.value)}
                                                    className={`font-mono text-sm ${inputClassName}`}
                                                />
                                                <p className="text-xs text-slate-400">Path to AI definitions</p>
                                            </div>
                                        </div>

                                        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                                            <p className="text-sm text-amber-800">
                                                <strong>Note:</strong> You'll need to configure Bitbucket App credentials in your environment variables
                                                to enable repository synchronization.
                                            </p>
                                        </div>
                                    </motion.div>
                                )}

                                {!enableBitbucket && (
                                    <div className="p-6 rounded-lg bg-slate-50 border border-slate-200 text-center">
                                        <p className="text-slate-600">
                                            Bitbucket integration is optional. You can configure it later from the application settings.
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Footer */}
                <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex flex-row justify-between items-center">
                    <div>
                        {currentStep > 0 && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleBack();
                                }}
                                disabled={loading}
                                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                            >
                                <ChevronLeft size={16} />
                                Back
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onClose();
                            }}
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        {isLastStep ? (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSubmit();
                                }}
                                disabled={loading || !name.trim() || !slug.trim()}
                                className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        Create Application
                                        <Check size={16} />
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNext();
                                }}
                                disabled={!name.trim() || !slug.trim()}
                                className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>
                        )}
                    </div>
                </div>
        </SlideDrawer>
    );
}
